// Telling somebody a write LANDED.
//
// Every write in this product went through and said nothing, or said it inline in a
// paragraph the reader had already scrolled past. A leave request opened a process, a
// filing cleared a deadline, a decision settled an item — and the only evidence was
// that a list had quietly changed underneath.
//
// # A toast, and only for things that HAPPENED
//
// Not for validation, which belongs on the field that failed, and not for a refusal,
// which belongs where the reader can read riksu's own sentence and act on it. A toast
// is for "this is done and you can look away now" — the class of message that has no
// place on the screen once it has been read.
//
// # The failure path stays where it is
//
// `ProblemNotice` renders riksu's refusal, in place, with the detail the server wrote.
// Moving that into a toast would put a sentence somebody has to act on into a strip
// that disappears on a timer — so `failed` here is deliberately absent, and a caller
// that wants one is being told to render the problem instead.

import { toast } from "sonner";

/**
 * THE TOAST FOLLOWS RIKSU'S PALETTE AND NOT THE READER'S OPERATING SYSTEM.
 *
 * Reported as *"toast'teki bildirim yazıları okunmuyor"*, with `tools/check-contrast.mjs`
 * reporting **0 problems** on the same tree. Both were true, and the reason is that the pair
 * on screen was never a pair the gate could hold.
 *
 * # What was actually rendering
 *
 * `components/ui/sonner.tsx` calls `useTheme()` from `next-themes` and defaults to
 * `"system"` — and **there is no `ThemeProvider` anywhere in riksu**, so `theme` is
 * `undefined` and the default stands. Sonner then reads `prefers-color-scheme` itself and
 * stamps `data-sonner-theme="dark"` on the toaster on a machine set to dark.
 *
 * **Riksu has exactly ONE palette.** Measured across `src/`: no `prefers-color-scheme`, no
 * `.dark`, no `data-theme` — `next-themes` is imported by that one generated file and by
 * nothing else. So the toast was the only surface in the product that followed the OS.
 *
 * What that produced is not a toast that went dark, which would at least be consistent. The
 * generated component sets `--normal-bg` and `--normal-text` in an INLINE style, and an
 * inline style beats sonner's `[data-sonner-toaster][data-sonner-theme='dark']` block — so
 * the FILL stayed `--popover` (`#FFFFFF`) and the TITLE stayed `--popover-foreground`
 * (`#333333`, `color: inherit`), while the DESCRIPTION took
 * `[data-sonner-toaster][data-sonner-theme='dark'] [data-description] { color: hsl(0,0%,91%) }`
 * — a descendant selector on a different element, which riksu's variables never reach.
 * **`#E8E8E8` on `#FFFFFF` is 1.23:1**, and the description is where `done()` puts the half
 * of the sentence worth reading. That figure is the GATE's, read off its own output rather
 * than recalled: this paragraph said 1.19:1 for a day, which is nobody's arithmetic on this
 * pair and is exactly the wrong-value-that-reads-as-care the root `AGENTS.md` names.
 *
 * # Why the gate said nothing, and why that is not a threshold problem
 *
 * `check-contrast.mjs` measures riksu TOKEN against riksu TOKEN. Here one side is a **hex
 * literal shipped inside `node_modules/sonner/dist/styles.css`**, so the pair could not be
 * written in its table at all — the same shape as the 1:1 tooltip its own comments record
 * (*"the pair did not exist as a pair: one half came from a generated component and the
 * other from riksu"*) and as `ON_SHELL_CONTROL`'s class-merge, which that file names as
 * still ungated. The gate now reads sonner's shipped stylesheet and measures what it
 * hard-codes; see `THIRD_PARTY` there.
 *
 * # Two lines, and each closes a different half
 *
 * `theme` is the mechanism: pinned, the toast cannot resolve to a palette riksu does not
 * have, which also closes `--normal-bg-hover`, `--normal-border-hover` and the close
 * button's `--gray*` — none of which riksu sets and all of which the dark block moves.
 *
 * `classNames` is the colour: sonner's own literal is `#3f3f3f` in its light block, which
 * PASSES at 10.53:1 and is still not riksu's ink, so the description would be the one line in
 * the product whose colour came from a library. It is `text-shell-secondary` — the role a
 * secondary line already takes everywhere else, measured by the gate on this exact surface.
 *
 * Every ratio in this file is READ OFF `tools/check-contrast.mjs`, which is why both of them
 * were wrong in the same way and had to be corrected twice: this paragraph said 10.4:1 and the
 * one above said 1.19:1, and neither is what the WCAG formula gives for its pair. A figure
 * with two decimal places is a claim to have measured; recalling one to that precision is the
 * failure that reads as care. Run the gate and copy what it prints.
 *
 * **The `!` is not decoration and it is not a specificity fight.** Sonner's published
 * stylesheet is now a static bundle asset, while its redundant runtime injection is removed
 * by `vite.config.ts`; its rules are still UNLAYERED and riksu's are inside named layers.
 * Unlayered author styles outrank every `@layer`, so a rule in `@layer overrides` and a plain
 * utility would both lose whatever their specificity. An important declaration is the only
 * thing that wins on the criterion that comes first.
 */
export const TOASTER_THEME = "light" as const;

/**
 * The classes riksu puts on a toast's parts.
 *
 * Passing `toastOptions` at the call site REPLACES the generated component's own
 * (`{ toast: "cn-toast" }`), and that is deliberate rather than overlooked: nothing in this
 * repository defines `.cn-toast` — searched, zero rules — so it was a class name that
 * governed nothing. Declared here rather than inline in `app.tsx` so the contrast gate has
 * one place to read, and so the generated file is not edited.
 */
export const TOASTER_CLASSES = {
  toast:
    "rounded-(--radius-card)! border-line-default! bg-surface-panel! pr-12! text-shell-primary! shadow-(--shadow-overlay)!",
  content: "gap-0.5!",
  title: "text-shell-label! font-medium!",
  description: "text-shell-secondary! text-shell-caption!",
  icon: "text-shell-secondary!",
  success: "border-l-positive!",
  warning: "border-l-warning!",
  closeButton:
    "top-2! right-2! left-auto! size-8! transform-none! border-line-default! bg-surface-panel! text-shell-secondary! hover:bg-surface-hover! hover:text-shell-primary! focus-visible:ring-2! focus-visible:ring-focus!",
} as const;

/**
 * One semantic receipt has one on-screen instance.
 *
 * A double click, an idempotent replay, or two mounted readers observing the same landed
 * write must not produce a stack of identical sentences. Sonner resets the existing
 * receipt's lifetime when the same id is published again, which preserves the latest
 * feedback without making the reader dismiss duplicates.
 */
function receiptId(kind: "done" | "noted", message: string, description?: string): string {
  return `${kind}:${message}\u0000${description ?? ""}`;
}

/**
 * A write went through.
 *
 * `description` carries what riksu answered — the day count it quoted, how late a
 * filing was — because "saved" is the least interesting half of the sentence.
 */
export function done(message: string, description?: string): void {
  toast.success(message, { description, id: receiptId("done", message, description) });
}

/**
 * Something happened that is neither a success nor a refusal.
 *
 * The over-limit expense line is the case this exists for: riksu records it and
 * REPORTS it, because the ceiling is a customer policy rather than a statutory floor
 * and the judgement belongs to the approver. The write succeeded; the reader needs to
 * know something about it anyway.
 */
export function noted(message: string, description?: string): void {
  toast.warning(message, { description, id: receiptId("noted", message, description) });
}
